import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { getModuleIcon, getFamiliesForModule, getAllowedTiersForGroup, type ModuleId } from '../../../content';
import { getFamilyMasteryKey } from '../../../mastery';
import type { GroupId } from '../../../config/systemConfig';
import { Card } from '../../../components/ui/Card';
import { EmptyState } from '../../../components/shared/EmptyState';
import { StudentData } from './StudentDetailPanel';
import { PageLayout } from '../../shared/PageLayout';
import { getClassModuleStats } from '../../../utils/activityHelpers';
import { humanizeFamilyId } from '../../../content';
import { SubjectModuleRow } from '../../shared/SubjectModuleRow';
import { SubjectsSummaryHero } from '../../shared/SubjectsSummaryHero';
import { getMasteryBand, DEVELOPING_THRESHOLD } from '../../shared/masteryBands';
interface SubjectsPageProps {
  students: StudentData[];
  selectedGroup: GroupId;
  getModuleMastery: (learnerId: string, moduleId: string) => number;
  onSelectStudent: (studentId: string) => void;
}
type SortOption = 'attention' | 'strong' | 'alpha';
function getClassFamilyStats(students: StudentData[], moduleId: string, selectedGroup: number) {
  const families = getFamiliesForModule(moduleId as ModuleId);
  const allowedTiers = getAllowedTiersForGroup(selectedGroup as GroupId);
  const relevantFamilies = families.filter(f => allowedTiers.has(f.tier));
  return relevantFamilies.map(f => {
    const key = getFamilyMasteryKey(f.familyId, selectedGroup);
    let totalMastery = 0;
    let practicedCount = 0;
    const strugglingStudents: Array<{
      id: string;
      name: string;
      mastery: number;
    }> = [];
    students.forEach(s => {
      const fm = s.data.familyMastery?.[key];
      if (fm && fm.attempts > 0) {
        practicedCount++;
        totalMastery += fm.masteryScore;
        if (fm.masteryScore < DEVELOPING_THRESHOLD) {
          strugglingStudents.push({
            id: s.id,
            name: s.name,
            mastery: Math.round(fm.masteryScore)
          });
        }
      }
    });
    const avgMastery = practicedCount > 0 ? Math.round(totalMastery / practicedCount) : 0;
    return {
      familyId: f.familyId,
      label: humanizeFamilyId(f.familyId),
      avgMastery,
      practicedCount,
      strugglingStudents: strugglingStudents.sort((a, b) => a.mastery - b.mastery)
    };
  }).filter(f => f.practicedCount > 0).sort((a, b) => b.avgMastery - a.avgMastery);
}
function ExpandedModulePanelContent({
  moduleId,
  students,
  selectedGroup,
  strugglingStudents,
  onSelectStudent
}: {
  moduleId: string;
  students: StudentData[];
  selectedGroup: number;
  strugglingStudents: Array<{
    id: string;
    name: string;
    mastery: number;
  }>;
  onSelectStudent: (id: string) => void;
}) {
  const [selectedFamily, setSelectedFamily] = useState<string | null>(null);
  const familyStats = useMemo(() => {
    return getClassFamilyStats(students, moduleId, selectedGroup);
  }, [students, moduleId, selectedGroup]);
  return <div className="pl-4 sm:pl-18 pr-4 py-4 space-y-6" data-id="element-10188">
      {/* Family Table */}
      {familyStats.length > 0 && <div className="space-y-3" data-id="element-10189">
          <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider" data-id="element-10190">
            Onderwerpen ({familyStats.length})
          </div>
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-id="element-10191">
            <div className="divide-y divide-slate-100" data-id="element-10192">
              {familyStats.map(f => {
            const isFamilyExpanded = selectedFamily === f.familyId;
            return <div key={f.familyId} className="flex flex-col" data-id="element-10193">
                    <div className="p-3 flex items-center gap-4 hover:bg-slate-50 cursor-pointer transition-colors" role="button" tabIndex={0} aria-expanded={isFamilyExpanded} onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setSelectedFamily(isFamilyExpanded ? null : f.familyId);
                }
              }} onClick={() => setSelectedFamily(isFamilyExpanded ? null : f.familyId)} data-id="element-10194">
                      <div className="flex-1 min-w-0" data-id="element-10195">
                        <div className="text-sm font-bold text-slate-900 truncate" data-id="element-10196">
                          {f.label}
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-10197">
                          {f.practicedCount} leerlingen
                        </div>
                      </div>
                      <div className="w-32 flex items-center gap-2" data-id="element-10198">
                        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-10199">
                          <div className={`h-full rounded-full ${getMasteryBand(f.avgMastery).barClass}`} style={{
                      width: `${Math.max(2, f.avgMastery)}%`
                    }} data-id="element-10200" />
                        </div>
                        <div className={`w-8 text-right text-xs font-bold ${getMasteryBand(f.avgMastery).textClass}`} data-id="element-10201">
                          {f.avgMastery}%
                        </div>
                      </div>
                      <div className="w-6 flex justify-end text-slate-400" data-id="element-10202">
                        {isFamilyExpanded ? <ChevronUp className="w-4 h-4" data-id="element-10203" /> : <ChevronDown className="w-4 h-4" data-id="element-10204" />}
                      </div>
                    </div>

                    <AnimatePresence data-id="element-10205">
                      {isFamilyExpanded && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="overflow-hidden bg-slate-50/50 border-t border-slate-100" data-id="element-10206">
                          <div className="p-3 pl-4" data-id="element-10207">
                            {f.strugglingStudents.length === 0 ? <div className="text-xs font-medium text-emerald-600" data-id="element-10208">
                                Geen leerlingen met moeite.
                              </div> : <div className="space-y-2" data-id="element-10209">
                                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider" data-id="element-10210">
                                  Aandacht nodig ({f.strugglingStudents.length})
                                </div>
                                <div className="flex flex-wrap gap-2" data-id="element-10211">
                                  {f.strugglingStudents.map(s => {
                          const initial = s.name.charAt(0).toUpperCase();
                          return <div key={s.id} role="button" tabIndex={0} onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              e.stopPropagation();
                              onSelectStudent(s.id);
                            }
                          }} onClick={e => {
                            e.stopPropagation();
                            onSelectStudent(s.id);
                          }} className="flex items-center gap-2 px-2 py-1.5 bg-white border border-slate-200 rounded-lg hover:border-indigo-200 cursor-pointer shadow-sm transition-colors" data-id="element-10212">
                                        <div className="w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-[10px] font-bold" data-id="element-10213">
                                          {initial}
                                        </div>
                                        <span className="text-xs font-bold text-slate-900" data-id="element-10214">
                                          {s.name}
                                        </span>
                                        <span className="text-xs font-black text-rose-600" data-id="element-10215">
                                          {s.mastery}%
                                        </span>
                                      </div>;
                        })}
                                </div>
                              </div>}
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>;
          })}
            </div>
          </div>
        </div>}

      {/* Module Struggling Students */}
      {strugglingStudents.length === 0 ? <div className="text-sm font-medium text-emerald-600 flex items-center gap-2" data-id="element-10216">
          Alle leerlingen op schema voor dit vak ✓
        </div> : <div className="space-y-2" data-id="element-10217">
          <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2" data-id="element-10218">
            Aandacht nodig voor hele vak ({strugglingStudents.length})
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2" data-id="element-10219">
            {strugglingStudents.slice(0, 5).map(s => {
          const initial = s.name.charAt(0).toUpperCase();
          return <div key={s.id} role="button" tabIndex={0} onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              e.stopPropagation();
              onSelectStudent(s.id);
            }
          }} onClick={e => {
            e.stopPropagation();
            onSelectStudent(s.id);
          }} className="flex items-center justify-between p-2 rounded-lg bg-white border border-slate-200 hover:border-indigo-200 cursor-pointer transition-colors" data-id="element-10220">
                  <div className="flex items-center gap-2" data-id="element-10221">
                    <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold" data-id="element-10222">
                      {initial}
                    </div>
                    <span className="text-sm font-bold text-slate-900 truncate max-w-[100px]" data-id="element-10223">
                      {s.name}
                    </span>
                  </div>
                  <div className="text-xs font-black text-rose-600" data-id="element-10224">
                    {s.mastery}%
                  </div>
                </div>;
        })}
          </div>
          {strugglingStudents.length > 5 && <div className="text-xs font-medium text-slate-500 mt-2" data-id="element-10225">
              + {strugglingStudents.length - 5} meer
            </div>}
        </div>}
    </div>;
}
export function SubjectsPage({
  students,
  selectedGroup,
  getModuleMastery,
  onSelectStudent
}: SubjectsPageProps) {
  const [selectedModule, setSelectedModule] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<SortOption>('attention');
  const data = useMemo(() => {
    if (students.length === 0) return null;
    const moduleStats = getClassModuleStats(students, selectedGroup, getModuleMastery);
    const sortedStats = [...moduleStats].sort((a, b) => {
      if (sortBy === 'attention') {
        // Primary sort: declining trend
        if (a.trend === 'down' && b.trend !== 'down') return -1;
        if (b.trend === 'down' && a.trend !== 'down') return 1;
        // Secondary sort: lowest mastery
        return a.avgMastery - b.avgMastery;
      }
      if (sortBy === 'strong') {
        return b.avgMastery - a.avgMastery;
      }
      // alpha
      return a.moduleTitle.localeCompare(b.moduleTitle);
    });
    return {
      moduleStats: sortedStats
    };
  }, [students, getModuleMastery, selectedGroup, sortBy]);
  if (!data) {
    return <PageLayout title="Vakken" description="Analyse per vak" data-id="element-10226">
        <EmptyState title="Geen data beschikbaar" message="Er is nog geen data beschikbaar voor deze klas." emotion="thinking" colorScheme="slate" data-id="element-10227" />
      </PageLayout>;
  }
  const classAverage = data.moduleStats.length > 0 ? Math.round(data.moduleStats.reduce((acc, stat) => acc + stat.avgMastery, 0) / data.moduleStats.length) : 0;
  const attentionCount = data.moduleStats.filter(stat => stat.avgMastery < DEVELOPING_THRESHOLD).length;
  const handleAttentionClick = () => {
    // Find first module with attention needed
    const firstAttentionModule = data.moduleStats.find(stat => stat.avgMastery < DEVELOPING_THRESHOLD);
    if (firstAttentionModule) {
      setSelectedModule(firstAttentionModule.moduleId);
      // Small delay to allow expansion before scrolling
      setTimeout(() => {
        const el = document.getElementById(`module-${firstAttentionModule.moduleId}`);
        if (el) {
          el.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      }, 100);
    }
  };
  return <PageLayout title="Vakken" description="Analyse per vak" data-id="element-10228">
      <div className="max-w-5xl mx-auto space-y-6 pb-12" data-id="element-10229">
        <SubjectsSummaryHero variant="teacher" studentCount={students.length} classAverage={classAverage} attentionCount={attentionCount} onAttentionClick={handleAttentionClick} data-id="element-10230" />

        <div className="flex items-center justify-between mt-8 mb-4" data-id="element-10231">
          <h3 className="text-lg font-black text-slate-900" data-id="element-10232">Alle vakken</h3>
          <select value={sortBy} onChange={e => setSortBy(e.target.value as SortOption)} className="text-sm border-slate-200 rounded-lg bg-white text-slate-700 font-medium focus:ring-indigo-500 focus:border-indigo-500 py-1.5 pl-3 pr-8 shadow-sm" data-id="element-10233">
            <option value="attention" data-id="element-10234">Aandacht eerst</option>
            <option value="strong" data-id="element-10235">Sterk eerst</option>
            <option value="alpha" data-id="element-10236">Alfabetisch</option>
          </select>
        </div>

        <Card className="border-slate-200 shadow-sm overflow-hidden bg-white" data-id="element-10237">
          <div className="divide-y divide-slate-100" data-id="element-10238">
            {data.moduleStats.map(stat => {
            const IconComp = getModuleIcon(stat.moduleIcon);
            const isExpanded = selectedModule === stat.moduleId;
            const strugglingStudents = students.map(s => ({
              ...s,
              mastery: getModuleMastery(s.id, stat.moduleId)
            })).filter(s => s.mastery < DEVELOPING_THRESHOLD).sort((a, b) => a.mastery - b.mastery);
            return <div key={stat.moduleId} id={`module-${stat.moduleId}`} data-id="element-10239">
                  <SubjectModuleRow variant="teacher" moduleId={stat.moduleId} moduleTitle={stat.moduleTitle} moduleIcon={<IconComp className="w-5 h-5" data-id="element-10241" />} mastery={stat.avgMastery} trend={stat.trend} distribution={{
                strong: stat.distribution.strong,
                solid: stat.distribution.solid,
                developing: stat.distribution.developing,
                attention: stat.distribution.attention,
                total: stat.distribution.strong + stat.distribution.solid + stat.distribution.developing + stat.distribution.attention
              }} practicedCount={stat.practicedCount} isExpanded={isExpanded} onToggle={() => setSelectedModule(isExpanded ? null : stat.moduleId)} data-id="element-10240">
                    <ExpandedModulePanelContent moduleId={stat.moduleId} students={students} selectedGroup={selectedGroup} strugglingStudents={strugglingStudents} onSelectStudent={onSelectStudent} data-id="element-10242" />
                  </SubjectModuleRow>
                </div>;
          })}
          </div>
          <div className="bg-slate-50 p-3 border-t border-slate-100 flex flex-wrap justify-center gap-x-6 gap-y-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider" data-id="element-10243">
            <div className="flex items-center gap-1.5" data-id="element-10244">
              <div className="w-2.5 h-2.5 rounded-sm bg-emerald-500" data-id="element-10245" /> Sterk
            </div>
            <div className="flex items-center gap-1.5" data-id="element-10246">
              <div className="w-2.5 h-2.5 rounded-sm bg-indigo-500" data-id="element-10247" /> Goed op
              weg
            </div>
            <div className="flex items-center gap-1.5" data-id="element-10248">
              <div className="w-2.5 h-2.5 rounded-sm bg-amber-500" data-id="element-10249" /> Aan het
              groeien
            </div>
            <div className="flex items-center gap-1.5" data-id="element-10250">
              <div className="w-2.5 h-2.5 rounded-sm bg-rose-500" data-id="element-10251" /> Aandacht
            </div>
          </div>
        </Card>
      </div>
    </PageLayout>;
}